// src/utils/resolveMember.js
import { doc, getDoc } from 'firebase/firestore';

// Resolve the signed-in Firebase auth user to their canonical member identity.
// If this login was linked into another account (its user doc carries
// `mergedIntoUid`), follow that pointer so the member sees the same profile,
// athletes, and history no matter which linked email they signed in with.
// Falls back safely to the auth user's own identity when there's no doc,
// no merge pointer, or the pointer is dangling.
export async function resolveMember(db, authUser) {
  const fallback = { uid: authUser.uid, email: authUser.email || '', data: null, isAlias: false };
  try {
    const ownSnap = await getDoc(doc(db, 'users', authUser.uid));
    if (!ownSnap.exists()) return fallback;
    const own = ownSnap.data();
    const canonUid = own.mergedIntoUid;
    if (!canonUid || canonUid === authUser.uid) {
      return { uid: authUser.uid, email: own.email || authUser.email || '', data: own, isAlias: false };
    }
    const canonSnap = await getDoc(doc(db, 'users', canonUid));
    if (!canonSnap.exists()) {
      return { uid: authUser.uid, email: own.email || authUser.email || '', data: own, isAlias: false };
    }
    const canon = canonSnap.data();
    return { uid: canonUid, email: canon.email || authUser.email || '', data: canon, isAlias: true };
  } catch (e) {
    return fallback;
  }
}

// The canonical uid for a bare id, when there is no auth user object to hand —
// a write that has to land on the real account, or a helper that takes a uid.
// Returns the id unchanged when there is no pointer, when it dangles, or on any
// error, so a caller can always use what it gets back.
export async function canonicalUid(db, uid) {
  if (!uid) return uid;
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    const target = snap.exists() ? snap.data().mergedIntoUid : null;
    if (!target || target === uid) return uid;
    const canonSnap = await getDoc(doc(db, 'users', target));
    return canonSnap.exists() ? target : uid;
  } catch (e) {
    return uid;
  }
}
